import React, { useEffect, useState } from 'react';
import { MapPin, Plus, X, Save, Edit2, Trash2, Loader2, AlertCircle, CheckCircle, Building2 } from 'lucide-react';
import siteService, { Site, SiteCreateDto } from '../services/siteService';
import { uyar, hata, onayla } from '../components/GlobalModal';

const emptyForm: SiteCreateDto & { isActive?: boolean } = {
  name: '',
  code: '',
  address: '',
  city: '',
};

const Sites: React.FC = () => {
  const [items, setItems] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Site | null>(null);
  const [form, setForm] = useState<any>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await siteService.getAll(false);
      if (res.success) setItems(res.data as any);
    } catch (e) {
      setMsg({ ok: false, text: 'Şantiyeler yüklenemedi' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm, isActive: true });
    setMsg(null);
    setShowModal(true);
  };

  const openEdit = (s: Site) => {
    setEditing(s);
    setForm({
      name: s.name || '',
      code: s.code || '',
      address: s.address || '',
      city: s.city || '',
      isActive: s.isActive,
    });
    setMsg(null);
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name?.trim()) {
      setMsg({ ok: false, text: 'Şantiye adı boş olamaz' });
      return;
    }
    setSaving(true);
    try {
      const id = editing?.id ?? editing?.siteID ?? 0;
      const res = editing
        ? await siteService.update(id, form)
        : await siteService.create(form);
      if (res.success) {
        await load();
        setShowModal(false);
        setMsg({ ok: true, text: editing ? 'Güncellendi' : 'Eklendi' });
        setTimeout(() => setMsg(null), 3000);
      } else {
        setMsg({ ok: false, text: (res as any).message || 'İşlem başarısız' });
      }
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.message || 'Hata oluştu' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s: Site) => {
    const id = s.id ?? s.siteID ?? 0;
    if (!(await onayla(`"${s.name}" şantiyesini silmek istiyor musunuz?`))) return;
    try {
      await siteService.delete(id);
      await load();
      setMsg({ ok: true, text: 'Silindi' });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setMsg({ ok: false, text: 'Silinemedi (bağlı departman/personel olabilir)' });
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center">
            <Building2 size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-800">Şantiyeler / Şubeler</h1>
            <p className="text-slate-500 text-sm">Çok şantiyeli/şubeli müşteriler için lokasyon yönetimi</p>
          </div>
        </div>
        <button
          onClick={openAdd}
          className="px-4 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 flex items-center gap-2 transition-all"
        >
          <Plus size={16} /> Yeni Şantiye
        </button>
      </div>

      {msg && (
        <div className={`px-4 py-3 rounded-xl text-sm flex items-center gap-2 ${
          msg.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                 : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {msg.ok ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {msg.text}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm">Henüz şantiye/şube yok</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">Ad</th>
                <th className="px-4 py-3">Kod</th>
                <th className="px-4 py-3">Şehir</th>
                <th className="px-4 py-3">Adres</th>
                <th className="px-4 py-3 text-center">Durum</th>
                <th className="px-4 py-3 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {items.map(s => (
                <tr key={s.id ?? s.siteID} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-bold text-slate-800 flex items-center gap-2">
                    <MapPin size={14} className="text-blue-500" />
                    {s.name}
                  </td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">{s.code || '-'}</td>
                  <td className="px-4 py-3 text-slate-600">{s.city || '-'}</td>
                  <td className="px-4 py-3 text-slate-500 max-w-xs truncate" title={s.address || ''}>{s.address || '-'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${s.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {s.isActive ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <button onClick={() => openEdit(s)} className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg"><Edit2 size={14} /></button>
                    <button onClick={() => remove(s)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black text-slate-800">{editing ? 'Şantiye Düzenle' : 'Yeni Şantiye'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Ad *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm"
                  placeholder="Örn: Merkez Şantiye" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Kod</label>
                  <input type="text" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm"
                    placeholder="MRK-01" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Şehir</label>
                  <input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm"
                    placeholder="Diyarbakır" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Adres</label>
                <textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm resize-none" />
              </div>
              {editing && (
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="checkbox" checked={!!form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                  Aktif
                </label>
              )}
            </div>
            <div className="flex gap-2 pt-3 border-t border-slate-100">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-50">
                İptal
              </button>
              <button onClick={save} disabled={saving} className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sites;
